import {
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  Input,
  SimpleGrid,
  Text,
  useToast,
  VStack,
  Box,
  Icon,
  useColorModeValue,
  Container,
  HStack
} from '@chakra-ui/react';
import { FaUser, FaIdCard, FaUserCog, FaHome } from 'react-icons/fa';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import React, { useEffect, useState } from 'react';
import {
  HTTP_STATUS_CODE_OK,
  TOAST_ERROR,
  TOAST_ERROR_TITLE,
  TOAST_PARTNER_DATA_UPDATE_DESCRIPTION_ERROR,
  TOAST_PARTNER_DATA_UPDATE_DESCRIPTION_SUCCESS,
  TOAST_SUCCESS,
  TOAST_SUCCESS_TITLE
} from '../constants';
import { getMember, updateMemberData } from '../services/member-service';
import ConfirmationDialog from '../components/confirmation-dialog';
import { Member } from '../models/member';
import { AlertStatus } from '@chakra-ui/alert';
import { isMobile } from 'react-device-detect';
import { useAuth } from '../components/auth';
import { Auth } from '../models/auth';

function MyData(): JSX.Element {
  const auth: Auth = useAuth();
  const [member, setMember] = useState<Member>();
  const toast = useToast();


  const cardBg = useColorModeValue('white', 'gray.800');
  const headerBg = useColorModeValue('green.100', 'green.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const secondaryTextColor = useColorModeValue('gray.600', 'gray.300');

  useEffect(() => {
    const getMemberData = async (): Promise<void> => {
      if (!auth.user?.selectedMember) { return; }
      const member = await getMember(auth.user.selectedMember?.id);
      if (member != null) {
        setMember(member);
      }
    };

    getMemberData().catch(() => {});
  }, []);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: member?.id,
      firstName: member?.firstName,
      lastName: member?.lastName,
      documentNumber: member?.documentNumber,
      gender: member?.gender?.value ?? '',
      memberNumber: member?.memberNumber,
      membershipDate: new Date(
        member != null ? member?.membershipDate : Date.now()
      )?.toISOString()
        .split('T')[0],
      type: member?.memberType?.value,
      status: member?.status?.value,
      birthdate: new Date(
        member != null ? member?.birthdate : Date.now()
      )?.toISOString()
        .split('T')[0],
      address: {
        street: member?.contact?.street,
        number: member?.contact?.number,
        floor: member?.contact?.floor,
        apartment: member?.contact?.apartment,
        postcode: member?.contact?.postcode,
        city: member?.contact?.city,
        state: member?.contact?.state,
        country: member?.contact?.country,
        homePhoneNumber: member?.contact?.homePhoneNumber,
        mobilePhoneNumber: member?.contact?.mobilePhoneNumber,
        email: member?.contact?.email
      },
    },
    validationSchema: Yup.object({}),
    onSubmit: async () => {
      const user = auth.user;
      const memberUpdate = formik.values as unknown as Member;

      const response = await updateMemberData(
        member?.id ?? '',
        memberUpdate
      );

      auth.setUser({...user, member: memberUpdate });

      let title = TOAST_ERROR_TITLE;
      let description = TOAST_PARTNER_DATA_UPDATE_DESCRIPTION_ERROR;
      let status: AlertStatus = TOAST_ERROR;
      if (response?.status === HTTP_STATUS_CODE_OK) {
        title = TOAST_SUCCESS_TITLE;
        description = TOAST_PARTNER_DATA_UPDATE_DESCRIPTION_SUCCESS;
        status = TOAST_SUCCESS;
      }

      toast({
        title,
        description,
        status,
        duration: 9000,
        isClosable: true,
        position: 'bottom-right'
      });
    }
  });

  return (
    <Container maxW="container.xl" py={6}>
      <form onSubmit={formik.handleSubmit}>
        <VStack spacing={6} align="stretch">
                  <Box
          bg={headerBg}
          p={6}
          borderRadius="lg"
          border="1px solid"
          borderColor={borderColor}
          boxShadow="sm"
        >
            <HStack>
              <Icon as={FaUser} color="green.500" boxSize={6} />
              <Heading color={textColor}>Mis datos</Heading>
            </HStack>
            <Text mt={2} color={secondaryTextColor}>
              Estos son tus datos. Si detectás un error en un dato no editable, por favor comunicate con nosotros así lo podemos modificar por vos.
            </Text>
          </Box>

          <Box
            bg={cardBg}
            p={6}
            borderRadius="lg"
            border="1px solid"
            borderColor={borderColor}
            boxShadow="lg"
          >
            <VStack spacing={4} align="stretch">
              <HStack>
                <Icon as={FaIdCard} color="green.500" />
                <Heading as="h3" size="lg" color={textColor}>
                  Datos personales
                </Heading>
              </HStack>

              <SimpleGrid spacing={5} columns={[1, 1, 2]}>
                <GridItem>
                  <FormControl>
                    <FormLabel color={textColor}>Nombres</FormLabel>
                    <Input
                      id="first-name"
                      name="firstName"
                      type="text"
                      value={formik.values.firstName}
                      onChange={formik.handleChange}
                      isDisabled
                      bg={useColorModeValue('gray.50', 'gray.700')}
                    />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel color={textColor}>Apellidos</FormLabel>
                    <Input
                      id="last-name"
                      name="lastName"
                      type="text"
                      value={formik.values.lastName}
                      onChange={formik.handleChange}
                      isDisabled
                      bg={useColorModeValue('gray.50', 'gray.700')}
                    />
                  </FormControl>
                </GridItem>
              </SimpleGrid>

              <SimpleGrid spacing={5} columns={[1, 1, 3]}>
                <GridItem>
                  <FormControl>
                    <FormLabel color={textColor}>
                      {isMobile ? 'Documento' : 'Número de documento'}
                    </FormLabel>
                    <Input
                      id="document-number"
                      name="documentNumber"
                      type="number"
                      value={formik.values.documentNumber}
                      onChange={formik.handleChange}
                      isDisabled
                      bg={useColorModeValue('gray.50', 'gray.700')}
                    />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel color={textColor}>
                      {isMobile ? 'Fecha nac.' : 'Fecha de nacimiento'}
                    </FormLabel>
                    <Input
                      id="birthdate"
                      name="birthdate"
                      type="date"
                      value={formik.values.birthdate}
                      onChange={formik.handleChange}
                      isDisabled
                      bg={useColorModeValue('gray.50', 'gray.700')}
                    />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel color={textColor}>Género</FormLabel>
                    <Input
                      id="gender"
                      name="gender"
                      type="text"
                      value={formik.values.gender}
                      onChange={formik.handleChange}
                      isDisabled
                      bg={useColorModeValue('gray.50', 'gray.700')}
                    />
                  </FormControl>
                </GridItem>
              </SimpleGrid>
            </VStack>
          </Box>

          <Box
            bg={cardBg}
            p={6}
            borderRadius="lg"
            border="1px solid"
            borderColor={borderColor}
            boxShadow="lg"
          >
            <VStack spacing={4} align="stretch">
              <HStack>
                <Icon as={FaUserCog} color="orange.500" />
                <Heading as="h3" size="lg" color={textColor}>
                  Datos de socio
                </Heading>
              </HStack>

              <SimpleGrid spacing={5} columns={[1, 2, 4]}>
                <GridItem>
                  <FormControl>
                    <FormLabel color={textColor}>N° socio</FormLabel>
                    <Input
                      id="member-number"
                      name="memberNumber"
                      type="number"
                      value={formik.values.memberNumber}
                      onChange={formik.handleChange}
                      isDisabled
                      bg={useColorModeValue('gray.50', 'gray.700')}
                    />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel color={textColor}>
                      {isMobile ? 'Fecha asoc.' : 'Fecha de asociación'}
                    </FormLabel>
                    <Input
                      id="membership-date"
                      name="membershipDate"
                      type="date"
                      value={formik.values.membershipDate}
                      onChange={formik.handleChange}
                      isDisabled
                      bg={useColorModeValue('gray.50', 'gray.700')}
                    />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel color={textColor}>
                      {isMobile ? 'Tipo' : 'Tipo de socio'}
                    </FormLabel>
                    <Input
                      id="membership-type"
                      name="type"
                      type="text"
                      value={formik.values.type}
                      onChange={formik.handleChange}
                      isDisabled
                      bg={useColorModeValue('gray.50', 'gray.700')}
                    />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel color={textColor}>Estado</FormLabel>
                    <Input
                      id="membership-status"
                      name="status"
                      type="text"
                      value={formik.values.status}
                      onChange={formik.handleChange}
                      isDisabled
                      bg={useColorModeValue('gray.50', 'gray.700')}
                    />
                  </FormControl>
                </GridItem>
              </SimpleGrid>
            </VStack>
          </Box>

          <Box
            bg={cardBg}
            p={6}
            borderRadius="lg"
            border="1px solid"
            borderColor={borderColor}
            boxShadow="lg"
          >
            <VStack spacing={4} align="stretch">
              <HStack>
                <Icon as={FaHome} color="purple.500" />
                <Heading as="h3" size="lg" color={textColor}>
                  Datos de contacto
                </Heading>
              </HStack>

              <Text fontSize="md" fontWeight="semibold" color={secondaryTextColor}>
                Dirección
              </Text>

              <Grid templateColumns={['repeat(1, 1fr)', 'repeat(3, 1fr)', 'repeat(5, 1fr)']} gap={5}>
                <GridItem colSpan={[1, 2, 2]}>
                  <FormControl>
                    <FormLabel color={textColor}>Calle</FormLabel>
                    <Input
                      id="street"
                      name="address.street"
                      type="text"
                      value={formik.values.address.street}
                      onChange={formik.handleChange}
                    />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel color={textColor}>Número</FormLabel>
                    <Input
                      id="number"
                      name="address.number"
                      type="number"
                      value={formik.values.address.number}
                      onChange={formik.handleChange}
                    />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel color={textColor}>Piso</FormLabel>
                    <Input
                      id="floor"
                      name="address.floor"
                      type="text"
                      value={formik.values.address.floor}
                      onChange={formik.handleChange}
                    />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel color={textColor}>Depto</FormLabel>
                    <Input
                      id="apartment"
                      name="address.apartment"
                      type="text"
                      value={formik.values.address.apartment}
                      onChange={formik.handleChange}
                    />
                  </FormControl>
                </GridItem>
                <GridItem colSpan={[1, 2, 2]}>
                  <FormControl>
                    <FormLabel color={textColor}>Localidad</FormLabel>
                    <Input
                      id="city"
                      name="address.city"
                      type="text"
                      value={formik.values.address.city}
                      onChange={formik.handleChange}
                    />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel color={textColor}>
                      {isMobile ? 'C. Postal' : 'Cód. postal'}
                    </FormLabel>
                    <Input
                      id="postcode"
                      name="address.postcode"
                      type="text"
                      value={formik.values.address.postcode}
                      onChange={formik.handleChange}
                    />
                  </FormControl>
                </GridItem>
                <GridItem colSpan={[1, 1, 2]}>
                  <FormControl>
                    <FormLabel color={textColor}>Provincia</FormLabel>
                    <Input
                      id="state"
                      name="address.state"
                      type="text"
                      value={formik.values.address.state}
                      onChange={formik.handleChange}
                    />
                  </FormControl>
                </GridItem>
                <GridItem colSpan={[1, 2, 2]}>
                  <FormControl>
                    <FormLabel color={textColor}>País</FormLabel>
                    <Input
                      id="country"
                      name="address.country"
                      type="text"
                      value={formik.values.address.country}
                      onChange={formik.handleChange}
                    />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel color={textColor}>
                      {isMobile ? 'Tel.' : 'Teléfono fijo'}
                    </FormLabel>
                    <Input
                      id="home-phone-number"
                      name="address.homePhoneNumber"
                      type="text"
                      value={formik.values.address.homePhoneNumber}
                      onChange={formik.handleChange}
                    />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel color={textColor}>
                      {isMobile ? 'Cel.' : 'Celular'}
                    </FormLabel>
                    <Input
                      id="mobile-phone-number"
                      name="address.mobileNumber"
                      type="text"
                      value={formik.values.address.mobilePhoneNumber}
                      onChange={formik.handleChange}
                    />
                  </FormControl>
                </GridItem>
                <GridItem colSpan={[1, 2, 2]}>
                  <FormControl>
                    <FormLabel color={textColor}>Correo electrónico</FormLabel>
                    <Input
                      id="email"
                      name="address.email"
                      type="text"
                      value={formik.values.address.email}
                      onChange={formik.handleChange}
                    />
                  </FormControl>
                </GridItem>
              </Grid>
            </VStack>
          </Box>


          <Box
            bg={cardBg}
            p={6}
            borderRadius="lg"
            border="1px solid"
            borderColor={borderColor}
            boxShadow="lg"
            textAlign="center"
          >
            <ConfirmationDialog
              buttonLabel="Modificar datos"
              dialogHeader="Confirmar modificación"
              dialogBody="¿Estás seguro que querés modificar tus datos?"
              submitAction={formik.submitForm}
            />
          </Box>
        </VStack>
      </form>
    </Container>
  );
}

export default MyData;
